import {NotNull, notNull} from 'shared/type'
import {isNullOrEmpty, mapEntries, notNullOrEmpty} from 'shared/collection'
import {ActionIcon, Button, Combobox as Combo, useCombobox} from '@mantine/core'
import {useDebouncedValue, useHotkeys} from '@mantine/hooks'
import {useState, useMemo, ReactNode} from 'react'
import {HiMagnifyingGlass} from 'react-icons/hi2'
import {isEmpty, pick, sortBy} from 'remeda'
import {QuestionHeader, QuestionSubquestions, QuestionTileItems} from './QuestionTile'
import {questionStatusMd} from 'shared/forecast/questionStatus'
import {HiX} from 'react-icons/hi'
import Fuse, {FuseResult} from 'fuse.js'
import {MarkText} from 'components/form/MarkText'
import {KbdComboLabel} from 'components/Keyboard'
import {QuestionAttachmentList} from './QuestionAttachment'
import {QuestionPageRow} from './useQuestions'

type MinQuestionItem = {
  selected?: boolean
} & Pick<
  QuestionPageRow,
  'created_at' | 'id' | 'subquestions' | 'forecasters_total' | 'forecasts_from' | 'forecasts_to'
> & {
    cfg: Pick<QuestionPageRow['cfg'], 'title' | 'subTitle' | 'statusAt' | 'status' | 'attachments'>
  }

export const useQuestionSelect = <T extends MinQuestionItem>({
  items,
  onChange,
  label = 'select question',
  showAllByDefault,
  disableHotkey,
  counts,
  countLabel
}: {
  items?: T[]
  onChange?: (mode: 'select' | 'deselect', id: string) => void
  label?: string
  showAllByDefault?: boolean
  disableHotkey?: boolean
  counts?: Partial<Record<string, number>>
  countLabel?: (count?: number) => ReactNode
}) => {
  const [search, setSearch] = useState('')
  const [searchDebounced] = useDebouncedValue(search, 300)
  const combo = useCombobox({
    onDropdownClose: () => {
      combo.resetSelectedOption()
      combo.focusTarget()
      setSearch('')
    },
    onDropdownOpen: () => {
      combo.focusSearchInput()
    }
  })
  const itemsForSearch = useMemo(
    () =>
      items?.map(q => ({
        ...q,
        attachments: mapEntries(q.cfg.attachments, (path, a) => (a ? {path, ...a} : undefined)).filter(notNull),
        count: counts?.[q.id]
      })),
    [items]
  )
  type SearchItem = NotNull<typeof itemsForSearch>[number]

  const searchResults = useMemo(() => {
    const sortItems = <T extends {score?: number; item: {count?: number}}>(items: T[]) =>
      sortBy(items, [i => (isNullOrEmpty(i.item.count) ? 0 : 1), 'desc'], [i => i.score ?? 0, 'asc'])

    if (!itemsForSearch) return []
    if (isEmpty(search) && showAllByDefault)
      return sortItems(
        itemsForSearch.map(
          i =>
            ({
              item: i,
              matches: [],
              score: 0
            }) as Pick<FuseResult<SearchItem>, 'item' | 'matches' | 'score'>
        )
      )
    const res = sortItems(
      new Fuse(itemsForSearch, {
        keys: [
          {name: 'cfg.title', weight: 1},
          {name: 'cfg.subTitle', weight: 0.5},
          {name: 'subquestions.title', weight: 0.8},
          {name: 'attachments.name', weight: 0.5}
        ],
        includeScore: true,
        includeMatches: true,
        threshold: 0.3,
        minMatchCharLength: 3,
        ignoreLocation: true,
        useExtendedSearch: true
      }).search(searchDebounced)
    )
    return res
  }, [searchDebounced, itemsForSearch])
  useHotkeys(disableHotkey ? [] : [['mod+k', () => combo.openDropdown()]])
  return {combo, search, setSearch, searchResults, items, onChange, label, disableHotkey, countLabel}
}
type UseQuestionSelect = ReturnType<typeof useQuestionSelect>

export const QuestionSelect = ({
  combo,
  search,
  setSearch,
  searchResults,
  onChange,
  items,
  label,
  disableHotkey,
  countLabel,
  children
}: UseQuestionSelect & {children?: ReactNode}) => {
  const selectedItems = items?.filter(i => i.selected)
  const noneSelected = selectedItems == null || selectedItems.length == 0
  return (
    <>
      {selectedItems?.map(({cfg: q, id}) => (
        <div key={id} className='bg-gray-50 rounded-lg font-semibold flex items-center max-w-[20em] '>
          <div className='hover:overflow-visible hover:whitespace-normal truncate text-gray-600 text-sm'>{q.title}</div>
          <ActionIcon onClick={() => onChange?.('deselect', id)}>
            <HiX />
          </ActionIcon>
        </div>
      ))}
      <Combo
        store={combo}
        position='bottom'
        width='70em'
        onOptionSubmit={id => {
          combo.closeDropdown()
          onChange?.('select', id)
        }}>
        <Combo.Target>
          <div>
            {children != null ?
              children
            : <Button
                size='compact-md'
                className='opacity-40'
                leftSection={<HiMagnifyingGlass />}
                onClick={() => combo.openDropdown()}
                rightSection={
                  noneSelected && !disableHotkey ? <KbdComboLabel combo={{mods: ['Control'], key: 'k'}} /> : undefined
                }>
                {noneSelected ? label : ''}
              </Button>
            }
          </div>
        </Combo.Target>
        <Combo.Dropdown className='overflow-y-auto !p-1 max-h-[80vh] !max-w-[50em]'>
          <Combo.Search
            size='md'
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder='Search questions'
          />
          <Combo.Options className='space-y-4'>
            {searchResults.length > 0 ?
              searchResults?.map(({item: {id, count, ...q}, matches}) => (
                <Combo.Option
                  key={id}
                  value={id}
                  className='data-[combobox-selected=true]:!bg-primary/10 data-[combobox-selected=true]:!text-gray-900'>
                  <div className='flex'>
                    <QuestionHeader
                      className='grow'
                      title={
                        <MarkText marks={matches?.find(m => m.key == 'cfg.title')?.indices}>{q.cfg.title}</MarkText>
                      }
                      subTitle={
                        <MarkText marks={matches?.find(m => m.key == 'cfg.subTitle')?.indices}>
                          {q.cfg.subTitle}
                        </MarkText>
                      }
                    />
                    {countLabel ? countLabel(count) : <div className='opacity-50 font-semibold'>{count} results</div>}
                  </div>
                  <div className='flex gap-4 items-center'>
                    <QuestionTileItems
                      statusItem={{md: questionStatusMd[q.cfg.status], value: q.cfg.status}}
                      {...pick(q, ['forecasters_total', 'forecasts_from', 'forecasts_to', 'created_at'])}
                    />
                  </div>
                  <QuestionAttachmentList
                    attachments={q.attachments.map(a => ({
                      ...a,
                      name: (
                        <MarkText marks={matches?.find(m => m.key == 'attachments.name')?.indices}>{a.name}</MarkText>
                      )
                    }))}
                  />
                  {notNullOrEmpty(search) && (
                    <QuestionSubquestions
                      question_id={id}
                      subquestions={q.subquestions.map((sq, i) => ({
                        ...sq,
                        title: (
                          <MarkText
                            marks={matches?.find(m => m.key == 'subquestions.title' && m.refIndex == i)?.indices}>
                            {sq.title}
                          </MarkText>
                        )
                      }))}
                    />
                  )}
                </Combo.Option>
              ))
            : search ?
              <Combo.Empty>Nothing found</Combo.Empty>
            : <Combo.Empty>Search to see results</Combo.Empty>}
          </Combo.Options>
        </Combo.Dropdown>
      </Combo>
    </>
  )
}
